'use client'

import { Typography } from '@mui/material'
import { useTranslations } from 'next-intl'

import Disclosure from './Disclosure'
import StepLabel, { emphasiseVerb } from './StepLabel'
import { splitDuration } from './duration'

type ThoughtRowProps = {
  text: string
  /** How long the thinking took; the label stays a plain "Thought" without it. */
  durationMs?: number
}

/** One finished reasoning part as a collapsed "Thought" row that opens to the model's summarized thinking. */
export default function ThoughtRow({ text, durationMs }: ThoughtRowProps) {
  const t = useTranslations('features.AssistantV2')
  const trimmed = text.trim()
  if (!trimmed) return null

  return (
    <Disclosure label={<StepLabel>{thoughtLabel()}</StepLabel>}>
      <Typography
        variant="body2"
        component="p"
        data-testid="assistant-reasoning"
        sx={{ m: 0, color: 'text.secondary', whiteSpace: 'pre-wrap' }}
      >
        {trimmed}
      </Typography>
    </Disclosure>
  )

  function thoughtLabel() {
    if (durationMs === undefined) return t.rich('thought', { verb: emphasiseVerb })
    const { minutes, seconds } = splitDuration(durationMs)
    if (minutes > 0) return t.rich('thoughtMinutes', { verb: emphasiseVerb, minutes, seconds })
    if (seconds > 0) return t.rich('thoughtSeconds', { verb: emphasiseVerb, seconds })
    return t.rich('thoughtBriefly', { verb: emphasiseVerb })
  }
}
